import { TextEditor } from '@approvalmax/ui';
import { Text } from '@approvalmax/ui/src/components';
import { intl } from '@approvalmax/utils';
import { xeroConstants } from 'modules/common/constants';
import { PaymentDetailsCell } from 'modules/components';
import { FC, memo, useCallback } from 'react';

import { columnWidth } from '../../BillsInformation.constants';
import { messages } from './LineItemsTableHeaderForSupplier.messages';
import { StyledCell, StyledTable } from './LineItemsTableHeaderForSupplier.styles';
import { LineItemsTableHeaderForSupplierProps } from './LineItemsTableHeaderForSupplier.types';

export const LineItemsTableHeaderForSupplier: FC<LineItemsTableHeaderForSupplierProps> = memo((props) => {
    const {
        name,
        companyId,
        requestId,
        reference = '',
        changeReferencesForMergedItems,
        showErrors,
        mergedBySupplierTotal,
        paymentDetailsDiscrepanciesOnChecking,
    } = props;

    const {
        currency,
        paymentAmount,
        paymentDetailsName,
        firstBillRequestId,
        contact: { id: contactId },
        paymentDetailsId,
        paymentDetailsStatus,
    } = mergedBySupplierTotal;

    const paymentDetailsDiscrepancy = paymentDetailsDiscrepanciesOnChecking?.find(
        (paymentDetailsDiscrepancy) =>
            paymentDetailsDiscrepancy.contactId === contactId &&
            paymentDetailsDiscrepancy.paymentDetailsDiscrepancy.paymentDetailsId === paymentDetailsId
    );

    const handleChangeReference = useCallback(
        (value: string) =>
            changeReferencesForMergedItems({
                [`${contactId},${paymentDetailsId}`]: value,
            }),
        [contactId, paymentDetailsId, changeReferencesForMergedItems]
    );

    return (
        <StyledTable>
            <thead>
                <tr>
                    <StyledCell $minWidth={columnWidth.item}>
                        <Text fontSize='small' font='label' fontWeight='medium'>
                            {name}
                        </Text>
                    </StyledCell>

                    <StyledCell $width={columnWidth.date + columnWidth.dueDate}>
                        <Text font='label' fontWeight='medium' fontSize='small'>
                            <TextEditor
                                value={reference}
                                maxLength={xeroConstants.amaxPayBatchPaymentMergeBySupplierReference}
                                onChange={handleChangeReference}
                                placeholder={messages.reference}
                                invalid={showErrors && !reference}
                            />
                        </Text>
                    </StyledCell>

                    <StyledCell
                        $textAlignRight
                        $width={columnWidth.paymentAmount + columnWidth.billCurrency + columnWidth.amountDue}
                    >
                        <Text fontSize='small' font='label' fontWeight='medium'>
                            {`${intl.formatCurrency(paymentAmount, currency)}`}
                        </Text>
                    </StyledCell>

                    <StyledCell
                        $width={columnWidth.paymentDetails + columnWidth.attachments + columnWidth.overflowMenu}
                        $additionalPaddingLeft
                    >
                        <PaymentDetailsCell
                            skipBillRequest
                            status={paymentDetailsStatus}
                            bankName={paymentDetailsName}
                            companyId={companyId}
                            requestId={requestId}
                            billRequestId={firstBillRequestId}
                            counterpartyId={contactId}
                            paymentDetailsId={paymentDetailsId}
                            paymentDetailsDiscrepancy={paymentDetailsDiscrepancy}
                        />
                    </StyledCell>
                </tr>
            </thead>
        </StyledTable>
    );
});

LineItemsTableHeaderForSupplier.displayName = 'LineItemsTableHeaderForSupplier';
